import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// JURASSIC PARK THEMED MODELS
// ============================================

export function JPGateModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  // Iconic Jurassic Park entrance gate
  return (
    <group>
      {/* Left pillar */}
      <mesh position={[-2, 2, 0]}>
        <cylinderGeometry args={[0.3, 0.4, 4, 8]} />
        <meshStandardMaterial color="#8b7355" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Right pillar */}
      <mesh position={[2, 2, 0]}>
        <cylinderGeometry args={[0.3, 0.4, 4, 8]} />
        <meshStandardMaterial color="#8b7355" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Top arch - wood */}
      <mesh position={[0, 4.2, 0]}>
        <boxGeometry args={[5, 0.4, 0.6]} />
        <meshStandardMaterial color="#5a4030" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Logo area */}
      <mesh position={[0, 4.8, 0]}>
        <cylinderGeometry args={[1, 1, 0.3, 16]} />
        <meshStandardMaterial color="#8b0000" roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Torch holders */}
      <mesh position={[-2, 3.5, 0.3]}>
        <cylinderGeometry args={[0.08, 0.1, 0.5, 6]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[2, 3.5, 0.3]}>
        <cylinderGeometry args={[0.08, 0.1, 0.5, 6]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Flames */}
      <pointLight position={[-2, 3.8, 0.3]} color="#ff6600" intensity={1} distance={3} />
      <pointLight position={[2, 3.8, 0.3]} color="#ff6600" intensity={1} distance={3} />
    </group>
  );
}

export function JPJeepModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  // Jurassic Park tour jeep
  const jeepDamaged = world === 'spectral';
  return (
    <group>
      {/* Chassis */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[1.2, 0.3, 2.2]} />
        <meshStandardMaterial color={jeepDamaged ? "#4a4a4a" : "#c41e3a"} roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Hood */}
      <mesh position={[0, 0.5, 0.8]} rotation={[-0.1, 0, 0]}>
        <boxGeometry args={[1, 0.15, 0.8]} />
        <meshStandardMaterial color={jeepDamaged ? "#3a3a3a" : "#c41e3a"} roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Cabin */}
      <mesh position={[0, 0.7, -0.2]}>
        <boxGeometry args={[1, 0.5, 1]} />
        <meshStandardMaterial color={jeepDamaged ? "#2a2a2a" : "#1a1a1a"} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roll cage */}
      {[[-0.45, -0.45], [0.45, -0.45], [-0.45, 0.25], [0.45, 0.25]].map(([x, z], i) => (
        <mesh key={`bar-${i}`} position={[x, 0.9, z]}>
          <cylinderGeometry args={[0.03, 0.03, 0.4, 6]} />
          <meshStandardMaterial color="#3a3a3a" metalness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Wheels */}
      {[[0.5, 0.6], [-0.5, 0.6], [0.5, -0.7], [-0.5, -0.7]].map(([x, z], i) => (
        <mesh key={`wheel-${i}`} position={[x, 0.15, z]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.2, 0.2, 0.15, 12]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Headlights */}
      <mesh position={[0.3, 0.4, 1.1]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#ffff88" emissive="#ffff00" emissiveIntensity={jeepDamaged ? 0 : 0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.3, 0.4, 1.1]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#ffff88" emissive="#ffff00" emissiveIntensity={jeepDamaged ? 0 : 0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* JP Logo stripe */}
      <mesh position={[0.61, 0.45, 0]}>
        <boxGeometry args={[0.02, 0.3, 1.5]} />
        <meshStandardMaterial color={jeepDamaged ? "#2a2a2a" : "#ffd700"} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Damage in spectral */}
      {jeepDamaged && (
        <>
          <mesh position={[0.3, 0.5, 0.5]} rotation={[0.5, 0.3, 0.2]}>
            <boxGeometry args={[0.3, 0.02, 0.4]} />
            <meshStandardMaterial color="#2a2a2a" transparent={isOccluding} opacity={opacity} />
          </mesh>
        </>
      )}
    </group>
  );
}

export function ElectricFenceModel({ world, isOccluding, opacity = 1, properties }: ModelProps) {
  // Jurassic Park electric fence
  const fenceLength = (properties?.length as number) || 6;
  return (
    <group>
      {/* Posts */}
      {[0, fenceLength].map((x, i) => (
        <group key={`post-${i}`} position={[x - fenceLength / 2, 0, 0]}>
          <mesh position={[0, 1.5, 0]}>
            <cylinderGeometry args={[0.08, 0.1, 3, 6]} />
            <meshStandardMaterial color="#4a4a4a" metalness={0.6} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {/* Insulators */}
          {[0.5, 1, 1.5, 2, 2.5].map((y, j) => (
            <mesh key={`ins-${j}`} position={[0.12, y, 0]}>
              <cylinderGeometry args={[0.04, 0.04, 0.08, 6]} />
              <meshStandardMaterial color="#f0f0f0" transparent={isOccluding} opacity={opacity} />
            </mesh>
          ))}
          {/* Warning sign */}
          <mesh position={[0, 1.8, 0.1]}>
            <boxGeometry args={[0.3, 0.2, 0.02]} />
            <meshStandardMaterial color="#ffcc00" transparent={isOccluding} opacity={opacity} />
          </mesh>
        </group>
      ))}
      {/* Wires */}
      {[0.5, 1, 1.5, 2, 2.5].map((y, i) => (
        <mesh key={`wire-${i}`} position={[0, y, 0]}>
          <boxGeometry args={[fenceLength, 0.01, 0.01]} />
          <meshStandardMaterial color="#888888" metalness={0.9} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
    </group>
  );
}

export function DinoNestModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  // Dinosaur nest with eggs
  return (
    <group>
      {/* Nest base */}
      <mesh position={[0, 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.5, 0.15, 8, 16]} />
        <meshStandardMaterial color="#8b7355" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Eggs */}
      {[[0, 0.15, 0], [0.2, 0.12, 0.15], [-0.15, 0.12, 0.2], [-0.2, 0.14, -0.1], [0.15, 0.13, -0.18]].map(([x, y, z], i) => (
        <mesh key={`egg-${i}`} position={[x, y, z]} rotation={[Math.random() * 0.3, 0, Math.random() * 0.3]}>
          <sphereGeometry args={[0.1, 8, 8]} />
          <meshStandardMaterial color="#f5f5dc" roughness={0.6} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Straw/debris */}
      <mesh position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.45, 0.5, 0.1, 12]} />
        <meshStandardMaterial color="#d2b48c" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

export function VisitorCenterModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  // Jurassic Park visitor center
  return (
    <group>
      {/* Main building */}
      <mesh position={[0, 1.5, 0]}>
        <boxGeometry args={[4, 3, 3]} />
        <meshStandardMaterial color="#d4c4a8" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 3.3, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[3.5, 1.5, 4]} />
        <meshStandardMaterial color="#8b4513" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Entrance */}
      <mesh position={[0, 0.8, 1.55]}>
        <boxGeometry args={[1.5, 1.6, 0.2]} />
        <meshStandardMaterial color="#4a3728" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Windows */}
      {[[-1.2, 1.5], [1.2, 1.5], [-1.2, 2.2], [1.2, 2.2]].map(([x, y], i) => (
        <mesh key={`win-${i}`} position={[x, y, 1.52]}>
          <boxGeometry args={[0.5, 0.4, 0.05]} />
          <meshStandardMaterial color="#87ceeb" metalness={0.3} roughness={0.1} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Columns */}
      {[-1.5, 1.5].map((x, i) => (
        <mesh key={`col-${i}`} position={[x, 0.8, 1.3]}>
          <cylinderGeometry args={[0.15, 0.18, 1.6, 8]} />
          <meshStandardMaterial color="#f5f5dc" roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
    </group>
  );
}

// ============================================
// MODEL REGISTRY
// ============================================

export const jurassicParkModels: ModelRegistry = {
  jp_gate: JPGateModel,
  jp_jeep: JPJeepModel,
  electric_fence: ElectricFenceModel,
  dino_nest: DinoNestModel,
  visitor_center: VisitorCenterModel,
};
